import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export interface DocTreeEntry {
  path: string
  type: 'blob' | 'tree'
}

interface DocsState {
  tree: DocTreeEntry[]
  lastSyncCommit: string | null
  loading: boolean
  error: string | null
  fileContents: Record<string, string>
  fileLoading: Record<string, boolean>
}

const initialState: DocsState = {
  tree: [],
  lastSyncCommit: null,
  loading: false,
  error: null,
  fileContents: {},
  fileLoading: {},
}

export const fetchDocsTree = createAsyncThunk(
  'docs/fetchTree',
  async (appId: string) => {
    const response = await fetch(`/api/app-docs/${appId}`)
    if (!response.ok) throw new Error('Failed to fetch docs')
    return response.json() as Promise<{
      tree: DocTreeEntry[]
      last_sync_commit: string | null
      synced: boolean
    }>
  },
)

export const fetchFileContent = createAsyncThunk(
  'docs/fetchFileContent',
  async ({ appId, path }: { appId: string; path: string }) => {
    const response = await fetch(`/api/app-docs/${appId}/file?path=${encodeURIComponent(path)}`)
    if (!response.ok) throw new Error('Failed to fetch file content')
    const data = await response.json() as { path: string; content: string }
    return data
  },
)

const docsSlice = createSlice({
  name: 'docs',
  initialState,
  reducers: {
    clearDocs() {
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocsTree.pending, (state) => {
        if (state.tree.length === 0) state.loading = true
        state.error = null
      })
      .addCase(fetchDocsTree.fulfilled, (state, action) => {
        state.loading = false
        state.tree = action.payload.tree
        state.lastSyncCommit = action.payload.last_sync_commit
      })
      .addCase(fetchDocsTree.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch docs'
      })
      .addCase(fetchFileContent.pending, (state, action) => {
        state.fileLoading[action.meta.arg.path] = true
      })
      .addCase(fetchFileContent.fulfilled, (state, action) => {
        state.fileLoading[action.payload.path] = false
        state.fileContents[action.payload.path] = action.payload.content
      })
      .addCase(fetchFileContent.rejected, (state, action) => {
        state.fileLoading[action.meta.arg.path] = false
      })
  },
})

export const { clearDocs } = docsSlice.actions
export default docsSlice.reducer
